import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { CookieConsentBanner } from './CookieConsentBanner';
import { hasValidConsent, hasCategory as checkCategory, getAcceptedCategories } from './consentCookie';
import { updateGoogleConsentMode } from './consentModeMapping';
import { detectConsentRegion, type ConsentRegion } from './geoDetect';
import { getIsImpersonating } from '../utils/tokenStore';

interface CookieConsentContextValue {
  showPreferences: () => void;
  hideBanner: () => void;
  hasCategory: (category: string) => boolean;
  isGdprRegion: boolean;
}

const CookieConsentContext = createContext<CookieConsentContextValue>({
  showPreferences: () => {},
  hideBanner: () => {},
  hasCategory: () => false,
  isGdprRegion: true,
});

export const useCookieConsent = () => useContext(CookieConsentContext);

export const CookieConsentProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [bannerState, setBannerState] = useState<{
    open: boolean;
    initialView: 'banner' | 'preferences';
    region: ConsentRegion;
  }>({ open: false, initialView: 'banner', region: 'gdpr' });

  useEffect(() => {
    detectConsentRegion().then((region) => {
      if (hasValidConsent()) {
        updateGoogleConsentMode(getAcceptedCategories());
        setBannerState((prev) => ({ ...prev, region }));
      } else {
        setBannerState({ open: true, initialView: 'banner', region });
      }
    });
  }, []);

  const hideBanner = useCallback(
    () => setBannerState((prev) => ({ ...prev, open: false })),
    []
  );

  const value: CookieConsentContextValue = {
    showPreferences: () =>
      setBannerState((prev) => ({ ...prev, open: true, initialView: 'banner' })),
    hideBanner,
    hasCategory: (cat: string) => checkCategory(cat),
    isGdprRegion: bannerState.region === 'gdpr',
  };

  return (
    <CookieConsentContext.Provider value={value}>
      {children}
      {bannerState.open && !getIsImpersonating() && (
        <CookieConsentBanner
          initialView={bannerState.initialView}
          region={bannerState.region}
          onClose={hideBanner}
        />
      )}
    </CookieConsentContext.Provider>
  );
};
